import React, { useEffect, useState } from 'react';
import { X, ArrowUp, ArrowDown, GripVertical, Loader2, ListOrdered, Check } from 'lucide-react';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { cn } from '../../lib/utils';
import { MenuItem } from '../../lib/types';
import { supabase } from '../../lib/supabase';
interface MenuReorderDrawerProps {
  restaurantId: string;
  isOpen: boolean;
  onClose: () => void;
}
export function MenuReorderDrawer({
  restaurantId,
  isOpen,
  onClose
}: MenuReorderDrawerProps) {
  const {
    categories,
    loading: catLoading
  } = useMenuCategories(restaurantId);
  const {
    items,
    loading: itemsLoading
  } = useMenuItems(restaurantId);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [localItems, setLocalItems] = useState<MenuItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  useEffect(() => {
    if (categories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);
  useEffect(() => {
    if (selectedCategoryId && items.length > 0) {
      const catItems = items.filter(i => i.category_id === selectedCategoryId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      setLocalItems(catItems);
    } else {
      setLocalItems([]);
    }
  }, [selectedCategoryId, items]);
  if (!isOpen) return null;
  const isLoading = catLoading || itemsLoading;
  const saveAllSortOrders = async (updatedItems: MenuItem[]) => {
    setSaving(true);
    try {
      await Promise.all(updatedItems.map((item, i) => supabase.from('menu_items').update({
        sort_order: i + 1
      }).eq('id', item.id)));
    } finally {
      setSaving(false);
    }
  };
  const handleMove = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === localItems.length - 1) return;
    const newItems = [...localItems];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newItems[index];
    newItems[index] = newItems[swapIndex];
    newItems[swapIndex] = temp;
    const updatedItems = newItems.map((item, i) => ({
      ...item,
      sort_order: i + 1
    }));
    setLocalItems(updatedItems);
    await saveAllSortOrders(updatedItems);
  };
  const handlePositionSubmit = async (itemId: string) => {
    const newPos = parseInt(editValue, 10);
    if (isNaN(newPos) || newPos < 1 || newPos > localItems.length) {
      setEditingId(null);
      return;
    }
    const currentIndex = localItems.findIndex(i => i.id === itemId);
    if (currentIndex === -1 || currentIndex === newPos - 1) {
      setEditingId(null);
      return;
    }
    // Remove item from current position and insert at new position
    const newItems = [...localItems];
    const [moved] = newItems.splice(currentIndex, 1);
    newItems.splice(newPos - 1, 0, moved);
    const updatedItems = newItems.map((item, i) => ({
      ...item,
      sort_order: i + 1
    }));
    setLocalItems(updatedItems);
    setEditingId(null);
    await saveAllSortOrders(updatedItems);
  };
  return <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity" onClick={onClose} data-id="element-438" />

      <div className="fixed inset-y-0 right-0 w-full sm:w-[500px] bg-[#141414] border-l border-white/10 shadow-2xl z-[101] flex flex-col transform transition-transform duration-300" data-id="element-439">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0" data-id="element-440">
          <div className="flex items-center gap-3" data-id="element-441">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center" data-id="element-442">
              <ListOrdered className="w-5 h-5 text-white/70" data-id="element-443" />
            </div>
            <div data-id="element-444">
              <h2 className="text-lg font-bold text-white leading-tight" data-id="element-445">
                Ordenar Productos
              </h2>
              <p className="text-xs text-white/40" data-id="element-446">
                Tocá el número para escribir la posición
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-447">
            {saving && <Loader2 className="w-4 h-4 text-white/40 animate-spin" data-id="element-448" />}
            <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors" data-id="element-449">
              <X className="w-5 h-5" data-id="element-450" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col" data-id="element-451">
          {isLoading ? <div className="flex-1 flex items-center justify-center" data-id="element-452">
              <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-453" />
            </div> : <>
              {/* Category Selector */}
              <div className="p-4 border-b border-white/10 shrink-0 overflow-x-auto" data-id="element-454">
                <div className="flex gap-2 min-w-max pb-1" data-id="element-455">
                  {categories.map(cat => <button key={cat.id} onClick={() => {
                setSelectedCategoryId(cat.id);
                setEditingId(null);
              }} className={cn('px-4 py-2 rounded-full text-sm font-bold transition-colors whitespace-nowrap', selectedCategoryId === cat.id ? 'bg-white text-black' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white')} data-id="element-456">
                      {cat.name}
                    </button>)}
                </div>
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4" data-id="element-457">
                {localItems.length === 0 ? <div className="text-center text-white/40 py-8" data-id="element-458">
                    No hay productos en esta categoría
                  </div> : <div className="flex flex-col gap-2" data-id="element-459">
                    {localItems.map((item, index) => <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl border bg-white/5 border-white/10 hover:border-white/20 transition-colors" data-id="element-460">
                        <div className="flex flex-col gap-1 shrink-0" data-id="element-461">
                          <button onClick={() => handleMove(index, 'up')} disabled={index === 0 || saving} className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors" data-id="element-462">
                            <ArrowUp className="w-3.5 h-3.5" data-id="element-463" />
                          </button>
                          <button onClick={() => handleMove(index, 'down')} disabled={index === localItems.length - 1 || saving} className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors" data-id="element-464">
                            <ArrowDown className="w-3.5 h-3.5" data-id="element-465" />
                          </button>
                        </div>

                        {/* Editable position number */}
                        {editingId === item.id ? <form onSubmit={e => {
                  e.preventDefault();
                  handlePositionSubmit(item.id);
                }} className="shrink-0" data-id="element-466">
                            <input type="number" min={1} max={localItems.length} value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => handlePositionSubmit(item.id)} autoFocus className="w-10 h-8 rounded-lg bg-white text-black text-center text-sm font-bold outline-none" data-id="element-467" />
                          </form> : <button onClick={() => {
                  setEditingId(item.id);
                  setEditValue(String(index + 1));
                }} className="w-10 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 hover:bg-white/20 transition-colors cursor-text" title="Clic para escribir posición" data-id="element-468">
                            <span className="text-sm font-bold text-white/60" data-id="element-469">
                              {index + 1}
                            </span>
                          </button>}

                        <div className="flex-1 min-w-0" data-id="element-470">
                          <div className="font-bold text-white truncate" data-id="element-471">
                            {item.name}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </>}
        </div>
      </div>
    </>;
}